import React from "react";
import { useLocalization } from "@i18n";
import { formatGeneralDisplayValue } from "../utils/formatGeneralDisplayValue";

export interface FieldItem {
  labelKey: string;
  value: unknown;
  isDate?: boolean;
}

export type GeneralDashboardFieldVariant = "default" | "identity" | "hydrology";

interface GeneralDashboardFieldGridProps {
  fields: FieldItem[];
  columns?: number;
  variant?: GeneralDashboardFieldVariant;
}

export const GeneralDashboardFieldGrid: React.FC<GeneralDashboardFieldGridProps> = ({
  fields,
  columns = 2,
  variant = "default",
}) => {
  const { translate } = useLocalization();

  if (variant === "identity") {
    return (
      <div className="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-3">
        {fields.map((field, index) => (
          <div
            key={`${field.labelKey}-${index}`}
            className="flex min-w-[67px] flex-col gap-0.5"
          >
            <span className="text-sm font-bold leading-normal text-foreground">
              {translate(field.labelKey)}
            </span>
            <span className="break-words text-sm font-normal leading-normal text-foreground">
              {formatGeneralDisplayValue(field.value, field.isDate)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "hydrology") {
    return (
      <div className="general-dashboard-hydrology-grid grid grid-cols-2 gap-x-2 gap-y-1 min-[768px]:grid-cols-4">
        {fields.map((field, index) => (
          <div key={`${field.labelKey}-${index}`} className="flex flex-col gap-0.5">
            <span className="text-[11px] font-bold leading-normal text-foreground">
              {translate(field.labelKey)}
            </span>
            <span className="break-words text-xs font-normal leading-normal text-muted-foreground">
              {formatGeneralDisplayValue(field.value, field.isDate)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const gridColsClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4",
  }[columns] || "grid-cols-1 sm:grid-cols-2";

  return (
    <div className={`grid gap-x-6 gap-y-4 ${gridColsClass}`}>
      {fields.map((field, index) => (
        <div key={`${field.labelKey}-${index}`} className="flex flex-col gap-0.5">
          <span className="text-sm font-bold leading-normal text-foreground">
            {translate(field.labelKey)}
          </span>
          <span className="break-words text-sm font-normal leading-normal text-foreground">
            {formatGeneralDisplayValue(field.value, field.isDate)}
          </span>
        </div>
      ))}
    </div>
  );
};
